import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Header, Loader, Accordion, Icon, Label, Button } from 'semantic-ui-react';
import { format } from 'date-fns';

import { indexRfisByProjectId, indexRfisByPortfolioId } from '~/api/requestForInformation';
import { useProjectContext, usePortfolioContext } from '~/hooks';
import RfiResponseForm from './RfiResponseForm';
import { ReactComponent as ChecklistComplete } from '~/assets/checklist-complete.svg';

const RfiQuestionnaire = () => {
  const [rfis, setRfis] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [activeIndices, setActiveIndices] = useState<number[]>([]);
  const { project } = useProjectContext();
  const { portfolio } = usePortfolioContext();

  useEffect(() => {
    if (portfolio) {
      getPortfolioRfis();
    } else {
      getProjectRfis();
    }
  }, []);

  const getPortfolioRfis = () => {
    if (portfolio?.id) {
      setLoading(true);
      indexRfisByPortfolioId(portfolio.id).then(resp => {
        setRfis(resp);
        setLoading(false);
      }).catch((error) => {
        toast.error('Failed to load RFIs');
        setLoading(false);
      });
    }
  };

  const getProjectRfis = () => {
    if (project?.id) {
      setLoading(true);
      indexRfisByProjectId(project.id).then(resp => {
        setRfis(resp);
        setLoading(false);
      }).catch((error) => {
        toast.error('Failed to load RFIs');
        setLoading(false);
      });
    }
  };

  const handleAccordionClick = (index: number) => {
    setActiveIndices(prevIndices =>
      prevIndices.includes(index)
        ? prevIndices.filter(i => i !== index)
        : [...prevIndices, index]
    );
  };

  const handleExpandAll = () => {
    setActiveIndices(rfis?.map((_: any, index: number) => index) || []);
  };

  const handleCollapseAll = () => {
    setActiveIndices([]);
  };

  const updateRfiInList = (rfiId: string, updatedResponse: any, action: 'create' | 'update' | 'delete') => {
    setRfis((prevRfis: any[]) => 
      prevRfis.map((rfi: any) => {
        if (rfi.id === rfiId) {
          switch (action) {
            case 'create':
            case 'update':
              return {
                ...rfi,
                responses: [updatedResponse]
              };
            case 'delete':
              return {
                ...rfi,
                responses: []
              };
            default:
              return rfi;
          }
        }
        return rfi;
      })
    );
  };

  const hasResponse = (rfi: any) => {
    return (rfi.responses && rfi.responses.length > 0);
  };

  const allRfisAnswered = rfis && Array.isArray(rfis) && rfis.length > 0 && rfis.every((rfi: any) => hasResponse(rfi));

    let headerText: any = 'Please provide the following supporting information:';
    if (allRfisAnswered) {
      headerText = <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><ChecklistComplete /> All questions have been answered!</div>;
    }

    return (
      <div id="supporting-info" className="rfi-questionnaire-container card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Header as='h2'>{headerText}</Header>
          {rfis && Array.isArray(rfis) && rfis.length > 0 && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                size="small"
                onClick={handleExpandAll}
                disabled={activeIndices.length === rfis.length}
              >
                Expand all
              </Button>
              <Button
                size="small"
                onClick={handleCollapseAll}
                disabled={activeIndices.length === 0}
              >
                Collapse all
              </Button>
            </div>
          )}
        </div>

        {/* {!allRfisAnswered && (
          <p>The developer has requested some additional information to help inform the bid review process. You can submit these responses at any time.</p>
        )} */}

        {loading ? (
          <Loader active inline='centered' />
        ) : (
          <>
            {rfis && Array.isArray(rfis) && rfis.length > 0 ? (
              <Accordion styled fluid>
                {rfis.map((rfi: any, index: number) => (
                  <div key={rfi.id}>
                    <Accordion.Title
                      active={activeIndices.includes(index)}
                      index={index}
                      onClick={() => handleAccordionClick(index)}
                      exclusive={false}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div>
                          <Icon name='dropdown' />
                          {rfi.name}
                        </div>
                        
                        {hasResponse(rfi) ? (
                          <div>
                            <span style={{ marginRight: '8px', fontSize: '0.9em', color: '#666', fontWeight: '400', fontStyle: 'italic' }}>
                              Last updated {format(new Date(rfi.responses[0].updatedAt || rfi.responses[0].createdAt), 'MMM d, yyyy')}
                            </span>
                          <Label color="green">
                            Answered
                          </Label>
                          </div>
                        ) : (
                          <Label color="grey" style={{ marginLeft: '8px' }}>
                            No response
                          </Label>
                        )}
                      </div>
                    </Accordion.Title>
                    <Accordion.Content active={activeIndices.includes(index)}>
                      <RfiResponseForm key={rfi.id} rfi={rfi} updateRfiInList={updateRfiInList} />
                    </Accordion.Content>
                  </div>
                ))}
              </Accordion>
            ) : (
              <p>No supporting information requests found for this {portfolio ? 'portfolio' : 'project'}.</p>
            )}
          </>
        )}
      </div>
    );
};

export default RfiQuestionnaire;